// src/hooks/useInventoryFiltersState.js
import { useCallback, useRef, useState } from "react";

const DEFAULT_TREND = { breakdown: "overall", seriesKeys: [], topN: 6 };

export default function useInventoryFiltersState() {
  // defaults as state so seedRange can update them (keeps isModified correct)
  const [defaultDateTo, setDefaultDateTo] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [defaultDateFrom, setDefaultDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const defaultPeriod = "D";

  // Applied
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [period, setPeriod] = useState(defaultPeriod);
  const [aggregated, setAggregated] = useState(true);
  const [selectedMaterials, setSelectedMaterials] = useState([]);

  // NEW: per-metric trend config
  const [trend, setTrend] = useState({
    lbs: { ...DEFAULT_TREND },
    pickups: { ...DEFAULT_TREND },
    cost: { ...DEFAULT_TREND },
  });

  // Draft
  const [draftDateFrom, setDraftDateFrom] = useState(defaultDateFrom);
  const [draftDateTo, setDraftDateTo] = useState(defaultDateTo);
  const [draftPeriod, setDraftPeriod] = useState(defaultPeriod);
  const [draftAggregated, setDraftAggregated] = useState(true);
  const [draftSelectedMaterials, setDraftSelectedMaterials] = useState([]);

  // NEW: per-metric trend config (draft)
  const [draftTrend, setDraftTrend] = useState({
    lbs: { ...DEFAULT_TREND },
    pickups: { ...DEFAULT_TREND },
    cost: { ...DEFAULT_TREND },
  });

  const isTrendModified = (tObj) => {
    const cmp = (x) =>
      x.breakdown !== DEFAULT_TREND.breakdown ||
      (Array.isArray(x.seriesKeys) && x.seriesKeys.length > 0) ||
      x.topN !== DEFAULT_TREND.topN;

    return cmp(tObj.lbs) || cmp(tObj.pickups) || cmp(tObj.cost);
  };

  const isModified =
    draftDateFrom !== defaultDateFrom ||
    draftDateTo !== defaultDateTo ||
    draftPeriod !== defaultPeriod ||
    draftAggregated !== true ||
    draftSelectedMaterials.length > 0 ||
    isTrendModified(draftTrend);

  const [refreshing, setRefreshing] = useState(false);

  const handleReset = () => {
    setDraftDateFrom(defaultDateFrom);
    setDraftDateTo(defaultDateTo);
    setDraftPeriod(defaultPeriod);
    setDraftAggregated(true);
    setDraftSelectedMaterials([]);

    setDraftTrend({
      lbs: { ...DEFAULT_TREND },
      pickups: { ...DEFAULT_TREND },
      cost: { ...DEFAULT_TREND },
    });
  };

  const handleApply = () => {
    setRefreshing(true);

    setTimeout(() => {
      setDateFrom(draftDateFrom);
      setDateTo(draftDateTo);
      setPeriod(draftPeriod);
      setAggregated(draftAggregated);
      setSelectedMaterials(draftSelectedMaterials);

      setTrend(draftTrend);

      setRefreshing(false);
    }, 120);
  };

  // called once after data loads to widen the window when 30-day default misses all data
  const seededRef = useRef(false);
  const seedRange = useCallback((from, to) => {
    if (seededRef.current) return;
    seededRef.current = true;
    setDefaultDateFrom(from);
    setDefaultDateTo(to);
    setDateFrom(from);
    setDateTo(to);
    setDraftDateFrom(from);
    setDraftDateTo(to);
  }, []);

  // --------- Convenience setters (applied) ----------
  const setTrendBreakdown = (metric, breakdown) => {
    setTrend((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        breakdown,
        seriesKeys: [], // reset when changing breakdown
      },
    }));
  };

  const setTrendSeriesKeys = (metric, seriesKeys) => {
    setTrend((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        seriesKeys: Array.isArray(seriesKeys) ? seriesKeys : [],
      },
    }));
  };

  const setTrendTopN = (metric, topN) => {
    setTrend((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        topN: Number(topN),
      },
    }));
  };

  // --------- Convenience setters (draft) ----------
  const setDraftTrendBreakdown = (metric, breakdown) => {
    setDraftTrend((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        breakdown,
        seriesKeys: [],
      },
    }));
  };

  const setDraftTrendSeriesKeys = (metric, seriesKeys) => {
    setDraftTrend((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        seriesKeys: Array.isArray(seriesKeys) ? seriesKeys : [],
      },
    }));
  };

  const setDraftTrendTopN = (metric, topN) => {
    setDraftTrend((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        topN: Number(topN),
      },
    }));
  };

  return {
    defaults: { defaultDateFrom, defaultDateTo, defaultPeriod },

    applied: {
      dateFrom,
      dateTo,
      period,
      aggregated,
      selectedMaterials,
      trend, // NEW
    },

    draft: {
      draftDateFrom,
      setDraftDateFrom,
      draftDateTo,
      setDraftDateTo,
      draftPeriod,
      setDraftPeriod,
      draftAggregated,
      setDraftAggregated,
      draftSelectedMaterials,
      setDraftSelectedMaterials,

      draftTrend, // NEW
      setDraftTrendBreakdown, // NEW
      setDraftTrendSeriesKeys, // NEW
      setDraftTrendTopN, // NEW
    },

    isModified,
    refreshing,
    handleReset,
    handleApply,
    seedRange,

    // applied-only in-page setters (like the trends section select)
    setters: {
      setTrendBreakdown,
      setTrendSeriesKeys,
      setTrendTopN,
    },
  };
}
